import { Component, ReactElement } from 'react';
import { connect } from 'react-redux';
import FeaturedStreamersActionCreator from '../../actions/featuredStreamersActionCreator';
import { areFieldsDifferent } from '../../../../../../shared/frontend/libs/utils';
import apiService from '../../services/apiService';
import MultiStreamerLayout from '../presentational/multiStreamPreview';
import { TApplicationState } from '../../libs/store';
import frontendLog from '../../libs/frontendLog';
import Skeleton from '../../../../../../shared/frontend/components/presentational/skeleton';
import { X_LARGE_SIZE } from '../presentational/streamMastheads/shared';
import { VideoDockedComponent } from '../../../../../../shared/frontend/components/ads/videoComponent';
import { Col } from '../../../../../../shared/frontend/components/grid/Col';
import { Row } from '../../../../../../shared/frontend/components/grid/Row';
import { FeatureFlagMap } from '../../../../../../shared/common/enums';
import { FeatureFlag } from '../../../../../../shared/gameConfig/sharedTypes';
import { isRwfRecapMastheadSelected } from '../presentational/streamMastheads/selection';

type TPropsFromState = {
  streamers: any[];
  isLoading: boolean;
  ui: any;
  forceMasthead?: string;
  currentFeatures: FeatureFlagMap;
};

type TPropsThatDispatch = {
  setFeaturedStreamers(type: string, params: any): void;
  setIsLoading(type: string, isLoading: boolean): void;
};

type TOwnProps = {
  type: string;
  className?: string;
  component?: any; // use this as the component to render with streamers
};

type TFeaturedStreamersProps = Partial<TPropsFromState> & Partial<TPropsThatDispatch> & TOwnProps;

/**
 * FeaturedStreamersContainer
 */
export class FeaturedStreamersContainer extends Component<TFeaturedStreamersProps> {
  public render(): ReactElement<TFeaturedStreamersProps> | null {
    const shouldRenderRwfRecap =
      !this.props.component && isRwfRecapMastheadSelected(this.props.currentFeatures);

    if (!shouldRenderRwfRecap && (this.props.isLoading || !this.props.streamers || this.props.streamers.length === 0)) {
      return (
        <Skeleton height={325} animate>
          <Row className="rio-full-height">
            <Col size={6}>
              <Skeleton width="unset" height={225} animate />
            </Col>
            <Col size={6}>
              {this.props.currentFeatures![FeatureFlag.NO_ADS] ? (
                <Skeleton width="unset" height={225} animate />
              ) : (
                <VideoDockedComponent
                  id="rio-docked-video"
                  inStreamPlacement={true}
                  slot="stream"
                  width={X_LARGE_SIZE.width}
                  height={X_LARGE_SIZE.height}
                  trackVisibility={true}
                />
              )}
            </Col>
          </Row>
        </Skeleton>
      );
    }

    const streamers = (this.props.streamers || []).map((stream: any) => ({ stream }));

    const { component: Component } = this.props;
    if (Component) {
      return <Component className={this.props.className} streamers={streamers} />;
    }

    return (
      <MultiStreamerLayout
        className={this.props.className}
        streamers={streamers}
        forceMasthead={this.props.forceMasthead}
      />
    );
  }
}

// take state from store and convert into props
function mapStateToProps(state: TApplicationState, ownProps: any): TPropsFromState {
  return {
    ...(state.featuredStreamers ? state.featuredStreamers[`TYPE_${ownProps.type}`] : {}),
    currentFeatures: state.currentFeatures || {}
  };
}

// return props that need access to `dispatch` function (props that can modify the store?)
function mapDispatchToProps(dispatch: Function, ownProps: TOwnProps): TPropsThatDispatch {
  return {
    setFeaturedStreamers(type: string, params: any) {
      dispatch(FeaturedStreamersActionCreator.setFeaturedStreamers(type, params));
    },
    setIsLoading(type: string, isLoading: boolean) {
      dispatch(FeaturedStreamersActionCreator.setIsLoading(type, isLoading));
    }
  };
}

// return one entire set of props
function mergeProps(stateProps: TPropsFromState, dispatchProps: TPropsThatDispatch, ownProps: TOwnProps) {
  const loadParams = {
    type: ownProps.type
  };

  const checkFields = ['type'];
  const contentChanged = !stateProps.ui || areFieldsDifferent(checkFields, loadParams, stateProps.ui);
  const shouldRenderRwfRecap = !ownProps.component && isRwfRecapMastheadSelected(stateProps.currentFeatures);

  if (!shouldRenderRwfRecap && contentChanged && !stateProps.isLoading) {
    dispatchProps.setIsLoading(loadParams.type, true);

    apiService.getFeaturedStreamers(loadParams, (err: any, result?: any) => {
      if (err) {
        frontendLog.error({ err }, 'getFeaturedStreamers failed');
        return;
      }

      if (!result) {
        frontendLog.error('getFeaturedStreamers empty');
        return;
      }

      dispatchProps.setFeaturedStreamers(loadParams.type, result);
      dispatchProps.setIsLoading(loadParams.type, false);
    });
  }

  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps
  };
}

export default connect<TPropsFromState, TPropsThatDispatch, TOwnProps>(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(FeaturedStreamersContainer);
